import { IconGridEmpty } from "@/assets/icons/geist/IconGridEmpty";
import { IconPlus } from "@/assets/icons/geist/IconPlus";
import { Button } from "@/components/flexkit/Button";
import { cn } from "@/components/podkit/lib/cn";
import { Heading1, Heading2 } from "@/components/podkit/typography/Headings";
import { Text } from "@/components/podkit/typography/Text";
import { CreateProjectModal } from "@/components/projects/CreateProjectModal";
import { ProjectSearchInput } from "@/components/projects/ProjectSearchInput";
import { ProjectsListPaginated } from "@/components/projects/ProjectsListPaginated";
import { useDocumentTitle } from "@/hooks/use-document-title";
import { useEnvironmentClassEntries } from "@/hooks/use-environment-class-entries";
import { useMayCreateProject } from "@/hooks/use-may-create-project";
import { useMembership } from "@/hooks/use-membership";
import { usePerformance } from "@/hooks/use-performance";
import { useListProjectsPaginated } from "@/queries/project-queries";
import { OrganizationRole } from "gitpod-next-api/gitpod/v1/organization_pb";
import { useCallback, useEffect, useMemo, useState, type FC } from "react";
import { Link, useLocation } from "react-router-dom";
import { useDebounce } from "use-debounce";

export const ProjectsPagePaginated: FC = () => {
    useDocumentTitle("Projects");
    const { markDataLoadStart, markDataLoadComplete } = usePerformance({
        component: "ProjectsPagePaginated",
    });
    const dataLoadStart = markDataLoadStart();

    const location = useLocation();
    const selectProjectId = (location.search && new URLSearchParams(location.search).get("project")) ?? undefined;
    const selectEnvironmentClass =
        (location.search && new URLSearchParams(location.search).get("environmentClass")) ?? undefined;
    const selectRunnerId = (location.search && new URLSearchParams(location.search).get("runnerId")) ?? undefined;
    const isFiltering = !!selectProjectId || !!selectEnvironmentClass || !!selectRunnerId;

    const [searchValue, setSearchValue] = useState<string>();
    const [debouncedSearchValue] = useDebounce(searchValue, 250, {
        trailing: true,
    });

    const { data, hasNextPage, fetchNextPage, isFetchingNextPage, isPending, isFetching } = useListProjectsPaginated({
        search: debouncedSearchValue,
    });

    const projectsData = useMemo(() => {
        if (!data) return { projects: [], isComplete: true };

        return {
            projects: data.pages.flatMap((page) => page.projects),
            isComplete: !data.pages[data.pages.length - 1]?.pagination?.nextToken,
        };
    }, [data]);

    const { mayCreateProject } = useMayCreateProject();

    const [showCreateProjectModal, setShowCreateProjectModal] = useState(false);

    const handleCreateProject = useCallback(() => {
        setShowCreateProjectModal(true);
    }, [setShowCreateProjectModal]);

    useEffect(() => {
        if (!isPending) {
            markDataLoadComplete(dataLoadStart);
        }
    }, [markDataLoadComplete, dataLoadStart, isPending]);

    const isEmpty = !isPending && projectsData.projects.length === 0 && !debouncedSearchValue?.trim();

    return (
        <div data-testid="projects-page-paginated" className="h-full">
            <div className="flex size-full flex-col gap-4">
                <div className="flex items-center">
                    <Heading1
                        className={cn(
                            "my-0.5", // keep sizing consistent with the button here, so that the UI doesn't jump when the button is shown
                            "flex grow flex-row align-middle text-content-primary",
                        )}
                    >
                        Projects
                    </Heading1>
                    {mayCreateProject && !isEmpty && (
                        <Button
                            LeadingIcon={IconPlus}
                            variant="primary"
                            onClick={handleCreateProject}
                            data-tracking-id="new-project-projects-page"
                        >
                            New Project
                        </Button>
                    )}
                </div>
                <>
                    <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                        <div className="relative flex w-full sm:flex-1">
                            <ProjectSearchInput
                                searchValue={searchValue}
                                setSearchValue={setSearchValue}
                                disabled={isFiltering}
                                isLoading={isFetching}
                            />
                        </div>
                    </div>
                    {isEmpty && !isPending && (
                        <div className="mt-[100px] flex size-full flex-col items-center justify-start gap-2">
                            <NoProjects handleCreateProject={handleCreateProject} />
                        </div>
                    )}
                    {!isEmpty && (
                        <ProjectsListPaginated
                            showRecentlyUsedOnly={true}
                            canEdit={true}
                            search={debouncedSearchValue}
                            selectProjectId={selectProjectId}
                            selectEnvironmentClass={selectEnvironmentClass}
                            selectRunnerId={selectRunnerId}
                            hasNextPage={hasNextPage}
                            fetchNextPage={fetchNextPage}
                            isFetchingNextPage={isFetchingNextPage}
                        />
                    )}
                </>
                {showCreateProjectModal && <CreateProjectModal onClose={() => setShowCreateProjectModal(false)} />}
            </div>
        </div>
    );
};

const NoProjects: FC<{
    handleCreateProject: () => void;
}> = ({ handleCreateProject }) => {
    const { membership } = useMembership();
    const isAdmin = membership?.userRole === OrganizationRole.ADMIN;
    const { mayCreateProject } = useMayCreateProject();

    const { environmentClassEntries, isLoading: isLoadingEnvClasses } = useEnvironmentClassEntries();
    const hasNoEnvClasses = !isLoadingEnvClasses && environmentClassEntries.length === 0;

    return (
        <>
            <IconGridEmpty className="mb-1 size-[90px] text-content-primary" />
            <Heading2>No projects</Heading2>
            <div className="max-w-[480px] pb-2">
                <Text className="text-center text-base">
                    Projects are pre-configured templates that provide one-click environments.
                </Text>
                {!isAdmin && (
                    <Text className="pt-2 text-center text-xs">
                        Only admins can create projects for your organization.
                    </Text>
                )}
            </div>
            {mayCreateProject && !isLoadingEnvClasses && (
                <>
                    {hasNoEnvClasses ? (
                        <Text className="pb-2 text-center text-base font-semibold">
                            Before you can create a project, you need to{" "}
                            <Link to="/settings/runners" className="underline">
                                set up a runner
                            </Link>
                            .
                        </Text>
                    ) : (
                        <Button
                            data-testid="create-project-button"
                            onClick={handleCreateProject}
                            data-tracking-id="create-project-no-projects"
                        >
                            Create project
                        </Button>
                    )}
                </>
            )}
        </>
    );
};
